/**
 * Shared stepper page header.
 *
 * The styling here is ChartMindHeader's, copied exactly — the toolbar mixin row
 * with pl/pr 2, the thin scrollbar on xs that disappears on sm+, the Stepper's
 * 520px xs scroll floor and 720px sm cap, the per-step icon colouring, and the
 * desktop-inline / mobile-floating split for the action buttons. ChartMindHeader
 * is the reference because it is the oldest and the one everything else was
 * already trying to look like.
 *
 * This exists so there is one copy. CareStartHeader was the second, VisitStart
 * became a third, and it drifted from both.
 *
 * An earlier version of this file dropped the maxWidth cap to make the steps
 * "full width". That was wrong: the cap is what keeps the labels at a readable
 * rhythm instead of stretching them across a 1400px monitor. Do not remove it.
 */

import React, { useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TopBarContext } from "context/TopBarContext";
import {
  TopBarActions,
  TopBarTitle,
} from "components/dashboard/layout/TopBarSlots";

export default function StepperPageHeader({
  steps,
  activeStep = 0,
  onStepClick = undefined,
  isStepDisabled = undefined,
  isStepCompleted = undefined,
  actions = null,
  minScrollWidth = 520,
  "data-testid": dataTestId = undefined,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollContainerRef = useRef(null);
  const topBar = useContext(TopBarContext);

  // On mobile, keep the active step centered in the scroll container.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;
    // Skip on desktop — only scroll when there's actual overflow (mobile).
    if (window.innerWidth >= theme.breakpoints.values.sm) return;
    const stepEls = container.querySelectorAll(".MuiStep-root");
    const activeEl = stepEls[activeStep];
    if (!activeEl) return;
    const elCenter = activeEl.offsetLeft + activeEl.offsetWidth / 2;
    container.scrollTo({
      left: elCenter - container.offsetWidth / 2,
      behavior: "smooth",
    });
  }, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const interactive = typeof onStepClick === "function";
  const completedFor = (step, index) =>
    isStepCompleted
      ? Boolean(isStepCompleted(step, index))
      : index < activeStep;

  // Horizontally scrollable on mobile.
  const stepper = (
    <Box
      ref={scrollContainerRef}
      sx={{
        flex: 1,
        minWidth: 0,
        overflowX: { xs: "auto", sm: "auto" },
        overflowY: { xs: "hidden", sm: "visible" },
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.palette.divider} transparent`,
        "&::-webkit-scrollbar": { height: 4 },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 2,
          backgroundColor: theme.palette.divider,
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        [theme.breakpoints.up("sm")]: {
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
        },
      }}
    >
      <Stepper
        activeStep={activeStep}
        nonLinear={interactive}
        sx={{
          minWidth: { xs: minScrollWidth, sm: "unset" },
          maxWidth: { sm: 720 },
          // Never wrap a step label. The row is a fixed-height toolbar, so a
          // label that wraps to two lines breaks the header's alignment
          // rather than just looking cramped. If labels stop fitting, the
          // container above already scrolls — that is the correct failure.
          // Keep step labels to one word, as ChartMind's are.
          "& .MuiStepLabel-label": { fontWeight: 700, whiteSpace: "nowrap" },
          "& .MuiStepLabel-label.Mui-active": { color: "primary.main" },
        }}
      >
        {steps.map((step, index) => {
          const label = typeof step === "string" ? step : step.label;
          const key = typeof step === "string" ? step : step.key || label;
          const isCompleted = completedFor(step, index);
          const disabled = isStepDisabled
            ? Boolean(isStepDisabled(step, index))
            : false;
          const isActive = index === activeStep;

          const stepIconColor = disabled
            ? theme.palette.action.disabled
            : isActive || !isCompleted
              ? theme.palette.primary.main
              : undefined;

          return (
            <Step
              key={key}
              completed={isCompleted}
              sx={{
                "& .MuiStepIcon-root": {
                  ...(stepIconColor &&
                    !isCompleted && { color: stepIconColor }),
                },
              }}
            >
              {interactive ? (
                <StepButton
                  onClick={() => onStepClick(step, index)}
                  disabled={disabled}
                >
                  {label}
                </StepButton>
              ) : (
                <StepLabel>{label}</StepLabel>
              )}
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );

  // In the dashboard the page has one top bar (DashboardNav), and this header lives
  // in it: the steps on its left on desktop, the icons before the app switcher at
  // every size. A phone is too narrow for steps beside the menu and icons, so there
  // the steps keep a row of their own under the bar.
  if (topBar) {
    return (
      <>
        {isMobile ? (
          <Box
            sx={{
              ...theme.mixins.toolbar,
              width: "100%",
              display: "flex",
              alignItems: "center",
              px: 2,
              flexShrink: 0,
            }}
            data-testid={dataTestId}
          >
            {stepper}
          </Box>
        ) : (
          <TopBarTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                alignSelf: "stretch",
                flex: 1,
                minWidth: 0,
              }}
              data-testid={dataTestId}
            >
              {stepper}
            </Box>
          </TopBarTitle>
        )}
        <TopBarActions>{actions}</TopBarActions>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          ...theme.mixins.toolbar,
          width: "100%",
          display: "flex",
          alignItems: "center",
          pl: 2,
          pr: 2,
          flexShrink: 0,
        }}
        data-testid={dataTestId}
      >
        {stepper}

        {/* Right: actions (desktop only — they float on mobile) */}
        {actions && !isMobile ? (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>

      {/* Floating buttons on mobile */}
      {actions && isMobile ? (
        <Box
          sx={{
            position: "fixed",
            top: 12,
            right: 16,
            zIndex: 1150,
            display: "flex",
            gap: 0.5,
          }}
        >
          {actions}
        </Box>
      ) : null}
    </>
  );
}

StepperPageHeader.propTypes = {
  /** Step labels, or `{ key, label }` when the label is not unique. */
  steps: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
    ]),
  ).isRequired,
  activeStep: PropTypes.number,
  /** Supply to make steps clickable (renders StepButton instead of StepLabel). */
  onStepClick: PropTypes.func,
  isStepDisabled: PropTypes.func,
  /** Defaults to "every step before the active one". */
  isStepCompleted: PropTypes.func,
  /** Icon actions. Inline on desktop, floating top-right on mobile. */
  actions: PropTypes.node,
  /** xs scroll floor. Raise it when there are more steps than ChartMind's seven. */
  minScrollWidth: PropTypes.number,
  "data-testid": PropTypes.string,
};
